import { memo } from 'react'
import type { NodeProps } from '@xyflow/react'
import { Handle, Position } from '@xyflow/react'
import { LAYOUTS } from '@shared/moodboard/layout'
import type { SavedBoard } from '../moodboard/savedBoard'
import { SkillGlyph } from '@shared/components/SkillGlyph'

/**
 * The Mood board card (Huanrui, 2026-10-16)
 * -------------------------------------------------------------
 * A frame wired in on the left; its pictures laid out as a 16:9 mood board,
 * made as a frame on the right -- the same frame the Mood board page's Copy
 * to project makes, so it can be moved about, changed and sent to Miro.
 *
 *  - Adjust opens the full-screen studio (moodboard/MoodboardStudio.tsx):
 *    the Mood board page's sheet and controls, without the search and the
 *    swap button -- the pictures are the frame's.
 *  - Apply makes the frame from what the card keeps. Every Apply makes a new
 *    frame; the earlier ones stay.
 *  - What was set in the studio stays on the card (`board`); a new card
 *    starts from the pictures' colours.
 */
export interface MoodboardNodeData extends Record<string, unknown> {
  skillRef: string
  name: string
  glyph: string
  glow: string
  open?: boolean
  /** The sheet, kept by picture (savedBoard.ts). */
  board?: SavedBoard
  running?: boolean
  progress?: string
  error?: string

  /* handed in by the board, never stored */
  /** How many frames are wired in, and how many pictures are in them. */
  frames?: number
  pictures?: number
  onEdit?: (patch: Partial<MoodboardNodeData>) => void
  onAdjust?: () => void
  onRun?: () => void
}

export const MoodboardNode = memo(function MoodboardNode({ data, selected }: NodeProps) {
  const card = data as MoodboardNodeData
  const isOpen = card.open !== false
  const running = card.running === true
  const frames = card.frames ?? 0
  const pictures = card.pictures ?? 0
  const ready = frames > 0 && pictures > 0
  const layout = card.board ? LAYOUTS.find((one) => one.kind === card.board!.kind)?.label : ''
  const edit = (patch: Partial<MoodboardNodeData>) => card.onEdit?.(patch)
  const why = frames === 0 ? 'Connect a frame first' : pictures === 0 ? 'The frame has no pictures in it' : ''

  return (
    <div
      className={[
        'cnode',
        'pnode',
        'gnode',
        isOpen ? 'pnode--open' : '',
        selected ? 'cnode--selected' : '',
        ready ? 'cnode--lit' : '',
        running ? 'cnode--running' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ '--tile-glow': card.glow } as React.CSSProperties}
    >
      <Handle type="target" id="in" position={Position.Left} className="cnode__port" />
      <Handle type="source" id="out" position={Position.Right} className="cnode__port" />

      <div className="pnode__head">
        <span className="cnode__glyph" aria-hidden="true">
          <SkillGlyph glyph={card.glyph} />
        </span>
        <span className="cnode__name">{card.name}</span>
        <button
          type="button"
          className="pnode__toggle nodrag"
          onClick={() => edit({ open: !isOpen })}
          title={isOpen ? 'Fold this card' : 'Unfold this card'}
          aria-label={isOpen ? 'Fold this card' : 'Unfold this card'}
        >
          {isOpen ? '−' : '+'}
        </button>
      </div>

      {isOpen ? (
        <div className="pnode__body">
          <p className="gnode__summary">
            {frames === 0
              ? 'No frame wired in'
              : `${pictures} picture${pictures === 1 ? '' : 's'} from ${frames === 1 ? 'the frame' : `${frames} frames`}`}
            {' · '}
            {card.board ? `${layout || 'laid out'}, kept` : 'not laid out yet'}
          </p>
          {card.error ? <p className="pnode__error">{card.error}</p> : null}
        </div>
      ) : null}

      {isOpen || running ? (
        <div className="gnode__actions">
          <button
            type="button"
            className="gnode__adjust nodrag"
            onClick={() => card.onAdjust?.()}
            disabled={!ready || running}
            title={why || 'Open the mood board full screen'}
          >
            Adjust
          </button>
          <button
            type="button"
            className="cnode__run pnode__run nodrag"
            onClick={() => card.onRun?.()}
            disabled={!ready || running}
            title={why || 'Make the mood board as a frame beside this card'}
          >
            {running ? `Making${card.progress ? ` ${card.progress}` : ''}…` : (
              <>
                Apply
                <span className="pnode__credits">free</span>
              </>
            )}
          </button>
        </div>
      ) : null}

      {!isOpen && card.error ? <span className="cnode__error">{card.error}</span> : null}
    </div>
  )
})
